import {
  Box,
  Checkbox,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchRounded from "@mui/icons-material/SearchRounded";
import ImageRounded from "@mui/icons-material/ImageRounded";
import { useEffect, useState } from "react";
import Button from "../../../../../components/common/Button";
import { BookingData } from "../../../types/view-booking";
import apiService from "../../../../../service/Api";
import DynamicThumbnailCard from "../../../../stream-management/components/thumbnail/ThumbnailCard";

interface AssetCategory {
  id: number;
  label: string;
  imageUrl?: string;
  customContent?: React.ReactNode;
}

interface GraphicsTabProps {
  bookingData: BookingData | null;
}

function AssetCard({
  category,
  checked,
  onCheck,
  onAction,
}: {
  category: AssetCategory;
  checked: boolean;
  onCheck: () => void;
  onAction: () => void;
}) {
  return (
    <Paper
      sx={{
        borderRadius: "16px",
        pt: 2.5,
        px: 3,
        pb: 4,
        boxShadow: 0,
        bgcolor: "#e8e8e8",
        flex: 1,
        minWidth: 0,
      }}
    >
      {/* Card header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Checkbox
            checked={checked}
            onChange={onCheck}
            size="small"
            sx={{
              p: 0,
              color: "#94a3b8",
              "&.Mui-checked": { color: "#724e41" },
            }}
          />
          <Typography
            sx={{ fontWeight: 700, fontSize: "14px", color: "#1e293b" }}
          >
            {category.label}
          </Typography>
        </Stack>

        <Button
          onClick={onAction}
          variant="contained"
          sx={{
            borderRadius: "8px",
            px: 2,
            py: 2,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "12px",
            minWidth: "unset",
            height: 28,
          }}
        >
          Action
        </Button>
      </Stack>

      {/* Image preview area */}
      <Box
        sx={{
          bgcolor: "#ffffff",
          borderRadius: "10px",
          height: 300,
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        {category.customContent ? (
          category.customContent
        ) : category.imageUrl ? (
          <img
            src={category.imageUrl}
            alt={category.label}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <ImageRounded sx={{ fontSize: 36, color: "#a0a0a0" }} />
          </Box>
        )}
      </Box>
    </Paper>
  );
}

export default function GraphicsTab({ bookingData }: GraphicsTabProps) {
  const [checkedIds, setCheckedIds] = useState<number[]>([]);
  const [search, setSearch] = useState("");
  const [streamId, setStreamId] = useState<number | null>(null);

  useEffect(() => {
    if (bookingData?.id) {
      apiService
        .get<{ results: { id: number }[] }>(
          `/api/streams/stream/?booking=${bookingData.id}`,
        )
        .then((res) => {
          const results = res.data?.results;
          if (results?.length > 0) setStreamId(results[0].id);
        })
        .catch(() => {});
    }
  }, [bookingData?.id]);

  const assetCategories: AssetCategory[] = [
    {
      id: 1,
      label: "Deceased",
      imageUrl: bookingData?.beloved?.avatar || undefined,
    },
    {
      id: 2,
      label: "Thumbnail",
      customContent: streamId ? (
        <DynamicThumbnailCard streamId={streamId} width={530} height={300} />
      ) : undefined,
    },
    { id: 3, label: "A1" },
    { id: 4, label: "Slideshow" },
    { id: 5, label: "Other" },
    { id: 6, label: "Other2" },
  ];

  const toggleCheck = (id: number) => {
    setCheckedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const filtered = assetCategories.filter((c) =>
    c.label.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <Box>
      {/* Top bar */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2.5 }}
      >
        <TextField
          placeholder="Search for assets"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchRounded sx={{ fontSize: 18, color: "#94a3b8" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: 300,
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              bgcolor: "white",
              fontSize: "13px",
              "& fieldset": { borderColor: "#e2e8f0" },
            },
          }}
        />

        <Button
          variant="contained"
          sx={{
            borderRadius: "8px",
            px: 3,
            height: 38,
          }}
        >
          Action
        </Button>
      </Stack>

      <Stack spacing={3}>
        <Stack direction="row" spacing={3}>
          {filtered.slice(0, 2).map((category) => (
            <AssetCard
              key={category.id}
              category={category}
              checked={checkedIds.includes(category.id)}
              onCheck={() => toggleCheck(category.id)}
              onAction={() => console.log("action", category.label)}
            />
          ))}
        </Stack>
        <Stack direction="row" spacing={3}>
          {filtered.slice(2, 4).map((category) => (
            <AssetCard
              key={category.id}
              category={category}
              checked={checkedIds.includes(category.id)}
              onCheck={() => toggleCheck(category.id)}
              onAction={() => console.log("action", category.label)}
            />
          ))}
        </Stack>
      </Stack>
    </Box>
  );
}
